import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { FitnessClassesDataService, FitnessClassesColumnsService } from '../../services';
import { DialogFitnessClassFormComponent } from './fitness-class-form/fitness-class-form.component';
import { DialogFitnessClassDetailsComponent } from './fitness-class-details/fitness-class-details.component';
import { FitnessClass, FitnessClassesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-fitness-classes',
  templateUrl: './fitness-classes.component.html',
  styleUrls: ['./fitness-classes.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class FitnessClassesComponent implements OnInit, OnDestroy {

  private fitnessClassAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private fitnessClassEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private fitnessClassDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public fitnessClassToDelete: FitnessClass = {};
  public fitnessClasses: FitnessClass[] = [];
  public fitnessClass: FitnessClass = {};
  public selectedFitnessClasses: FitnessClass[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private fitnessClassesDataService: FitnessClassesDataService,
    private fitnessClassesColumnsService: FitnessClassesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('FitnessClassesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Fitness Classes - Management');
    this.primengConfig.ripple = true;
    this.getFitnessClasses();
  }

  ngOnDestroy(): void {
    console.log('FitnessClassesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getFitnessClasses(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.fitnessClassesDataService.getFitnessClasses(this.parameters).subscribe((response: FitnessClassesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.fitnessClasses = response.results;
        } else {
          this.fitnessClasses = this.fitnessClasses.concat(response.results);
        }
        if (response.inlineCount > this.fitnessClasses.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getFitnessClasses();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSCLASSESNOTLOADED')});
    });
  }

  importFitnessClasses(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.fitnessClassesDataService.addFitnessClasses(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FITNESSCLASSESCREATED')});
                if (proceed === j) {
                  this.getFitnessClasses();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSCLASSESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSCLASSESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.fitnessClassesDataService.updateFitnessClasses(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FITNESSCLASSESUPDATED')});
                if (proceed === j) {
                  this.getFitnessClasses();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSCLASSESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSCLASSESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.fitnessClassesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('fitnessClasses.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedFitnessClasses() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Fitness Classes?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.fitnessClassesDataService.deleteFitnessClasses(this.selectedFitnessClasses).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Fitness Classes Deleted', life: 3000});
            this.getFitnessClasses();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Classes not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Classes not deleted'});
        });
        this.selectedFitnessClasses = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.fitnessClasses, this.columns, 'FitnessClasses');
  }

  exportExcel() {
    ToolsService.exportExcel(this.fitnessClasses, this.columns, 'FitnessClasses');
  }

  exportCsv() {
    ToolsService.exportCsv(this.fitnessClasses, this.columns, 'FitnessClasses');
  }

  refresh() {
    this.fitnessClassesDataService.clearCache();
    this.getFitnessClasses();
  }

  fitnessClassAdd(): void {
    if (!this.navigation.getParamValue('fitnessClasses.addInDialog', true)) {
      this.router.navigate(['/fitness-classes/add']);
      return;
    }
    this.fitnessClassAddDialog = this.dialogService.open(DialogFitnessClassFormComponent, {
      header: '', // this.translate.instant('NEWFITNESSCLASS'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        fitnessClass: {id: null},
        sports: this.fitnessClassesColumnsService.sports,
        ageRanges: this.fitnessClassesColumnsService.ageRanges,
        intensities: this.fitnessClassesColumnsService.intensities,
        classDurations: this.fitnessClassesColumnsService.classDurations,
        users: this.fitnessClassesColumnsService.users,
        fitnessGoals: this.fitnessClassesColumnsService.fitnessGoals,
      },
    });

    this.fitnessClassAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'FitnessClass added'});
        this.getFitnessClasses();
      }
    });
  }

  fitnessClassEdit(fitnessClass: FitnessClass): void {
    if (!this.navigation.getParamValue('fitnessClasses.editInDialog', true)) {
      this.router.navigate(['/fitness-classes/edit/' + fitnessClass.id]);
      return;
    }
    this.fitnessClassEditDialog = this.dialogService.open(DialogFitnessClassFormComponent, {
      header: '', // this.translate.instant('EDITFITNESSCLASS') + ' ' + fitnessClass.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        fitnessClass: fitnessClass,
        sports: this.fitnessClassesColumnsService.sports,
        ageRanges: this.fitnessClassesColumnsService.ageRanges,
        intensities: this.fitnessClassesColumnsService.intensities,
        classDurations: this.fitnessClassesColumnsService.classDurations,
        users: this.fitnessClassesColumnsService.users,
        fitnessGoals: this.fitnessClassesColumnsService.fitnessGoals,
      },
    });

    this.fitnessClassEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'FitnessClass updated'});
        this.getFitnessClasses();
      }
    });
  }

  fitnessClassDetails(fitnessClass: FitnessClass): void {
    if (!this.navigation.getParamValue('fitnessClasses.detailsInDialog', true)) {
      this.router.navigate(['/fitness-classes/details/' + fitnessClass.id]);
      return;
    }
    this.fitnessClassDetailsDialog = this.dialogService.open(DialogFitnessClassDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        fitnessClass: fitnessClass,
      }
    });
  }

  fitnessClassDelete(fitnessClass: FitnessClass): void {
    this.fitnessClassToDelete = fitnessClass;
    this.messageService.clear();
    this.messageService.add({
      key: 'fitnessClassDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + fitnessClass.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.fitnessClassesDataService.deleteFitnessClass(this.fitnessClassToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Fitness Class deleted'});
      this.getFitnessClasses();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Class not deleted'});
    });
  }

  onDeleteReject(): void {
    this.fitnessClassToDelete = {};
    this.messageService.clear('fitnessClassDelete');
  }

}
